import React, { useContext, useEffect, useMemo, useState } from 'react'

import clsx from 'clsx'

import styles from './style.module.scss'

import XText from 'v2/components/XText'
import { XTEXT_DECORATIONS, XTEXT_SIZE, XTEXT_MARGINS, BUTTON_STYLE_TYPE } from 'v2/styles/constants'

import XColors from '../../styles/colors'

import { OrganizationContext } from 'v2/context/organization'
import moment from 'moment-timezone'
import { MeQuery, useMyUpcomingPaymentLazyQuery } from 'types/graphql'
import { useTranslation } from 'i18n'
import { useAuthenticatedUser } from 'v2/context/user'

import ConditionalRender from 'utils/conditionalRender'
import XButton from 'v2/components/XButton'
import { useRouter } from 'next/router'

export interface Props {
    isAdminPanel: boolean
}

const XSideBarUserInfo: React.FC<Props> = (props: Props) => {
    const { authenticatedUser } = useAuthenticatedUser()
    const organizationContext = useContext(OrganizationContext)
    const [upcomindD, setUpcomingD] = useState(null)
    const { t } = useTranslation(window.LANG_NS)
    const router = useRouter()

    const [getUpcomingPayment, { data: upcomingData }] = useMyUpcomingPaymentLazyQuery()

    useEffect(() => {
        if (authenticatedUser) {
            if (upcomingData) {
                setUpcomingD(upcomingData)
            } else {
                getUpcomingPayment()
            }
        }
    }, [authenticatedUser, upcomingData])

    const formatDate = (date: any) => {
        const scheduledDate = moment(date).tz(window.ORGANIZATION_CONFIGS.timezone)
        if (scheduledDate.isSame(new Date(), 'day')) {
            return `${scheduledDate.format('MMM D, YYYY')}(${t('today')})`
        }
        if (scheduledDate.isSame(moment().add(1, 'day'), 'day')) {
            return `${scheduledDate.format('MMM D, YYYY')}(${t('tomorrow')})`
        }
        return scheduledDate.format('MMMM D, YYYY')
    }

    const upcomingPaymentDateStr = useMemo(() => {
        if (upcomindD && upcomindD.myUpcomingPayment && upcomindD.myUpcomingPayment?.scheduledDate) {
            return formatDate(upcomindD.myUpcomingPayment.scheduledDate)
        } else {
            return null
        }
    }, [upcomindD, t])

    const upcomingLastPaymentDateStr = useMemo(() => {
        if (upcomindD && upcomindD.myLastPayment) {
            return formatDate(upcomindD?.myLastPayment.scheduledDate)
        } else {
            return null
        }
    }, [upcomindD, t])

    const transferMethod = useMemo(() => {
        if (!authenticatedUser || !authenticatedUser.paymentProfile.transferMethods.edges.length) {
            return null
        } else {
            const edge = authenticatedUser.paymentProfile.transferMethods.edges.find(
                (transferMethodEdge: MeQuery['me']['paymentProfile']['transferMethods']['edges'][0]) =>
                    transferMethodEdge.node.isCurrentMethod
            )
            if (edge) {
                return edge.node
            } else {
                return null
            }
        }
    }, [authenticatedUser])

    const transferMethodText = useMemo(() => {
        if (!transferMethod) {
            return null
        } else {
            return transferMethod.oneLiner
        }
    }, [transferMethod, t])

    return (
        <div className={clsx(styles.container)}>
            <div className={styles.container__name}>
                <XText
                    size={XTEXT_SIZE.MEDIUM_BIGGER_BOLDED}
                    color={XColors.white2}
                    decorator={XTEXT_DECORATIONS.BOLD}
                    margins={XTEXT_MARGINS.NO_MARGINS}
                >
                    {authenticatedUser?.person?.profile?.firstName + ' ' + authenticatedUser?.person?.profile?.lastName}
                </XText>
            </div>

            <div className={styles.container__name}>
                <XText size={XTEXT_SIZE.SMALL_EXTRA} color={XColors.white2} margins={XTEXT_MARGINS.NO_MARGINS}>
                    {organizationContext.organization?.name}
                </XText>
            </div>

            <ConditionalRender if={!props.isAdminPanel}>
                <div className={styles.container__payment}>
                    <XText size={XTEXT_SIZE.SMALL_EXTRA} color={XColors.white2} margins={XTEXT_MARGINS.NO_MARGINS}>
                        {`${t('next_payment')}: ${upcomingPaymentDateStr || ''}`}
                    </XText>
                    <XText size={XTEXT_SIZE.SMALL_EXTRA} color={XColors.white2} margins={XTEXT_MARGINS.NO_MARGINS}>
                        {`${t('final_payment')}: ${upcomingLastPaymentDateStr || ''}`}
                    </XText>
                </div>

                <div className={styles.container__payment_method}>
                    <XText size={XTEXT_SIZE.SMALL_EXTRA} color={XColors.white2} margins={XTEXT_MARGINS.NO_MARGINS}>
                        {`${t('home_payment_method')}: ${transferMethodText}`}
                    </XText>
                </div>
            </ConditionalRender>

            <ConditionalRender if={props.isAdminPanel}>
                <XButton
                    styleType={BUTTON_STYLE_TYPE.PRIMARY}
                    onClick={() => {
                        router.push(
                            `${window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment}`
                        )
                    }}
                >
                    View As User
                </XButton>
            </ConditionalRender>
        </div>
    )
}

XSideBarUserInfo.displayName = 'XSideBarUserInfo'

export default XSideBarUserInfo
